import { MapDrawingConfig, drawSelectedRect } from "../canvas-drawing";
import {
  Backdrop,
  DataVisualization,
  GPSSource,
} from "PosterEditor/model/poster-project";
import { Point, Rect } from "PosterEditor/model/poster-types";
import { MapElement, DragHandle } from "../canvas-drawing";
import { pointIsInsideRect } from "../point-utils";
import { SnapLine } from "../rect-snapping";
import { rectWithPadding } from "../rect-utils";
import { combineHexWithOpacity } from "BEDROCK/utility/hex-utilities";
import { drawResizeHandle } from "./canvas-resize-handle";
import { DrawDataConfig, drawData } from "./canvas-drawing-data";

export const drawBackdrops = ({
  map,
  canvasElement,
  project,
  pointerLocation,
  elementToHighlight,
  selectedElement,
  gpsSource,
  imageCache,
}: MapDrawingConfig): {
  elementUnderPointerLocation: MapElement | undefined;
  snapLines: {
    vertical: SnapLine | undefined;
    horiztonal: SnapLine | undefined;
  };
} => {
  const ctx = canvasElement.current.getContext("2d");
  // ctx.globalCompositeOperation = "difference";

  let backdropUnderCursor:
    | {
        backdrop: Backdrop;
        rect: Rect;
        dragHandleUnderCursor?: DragHandle;
        point: Point;
      }
    | undefined;
  [...project.backdrops].reverse().forEach((backdrop) => {
    const isSelected = selectedElement?.backdrop?.id === backdrop.id;
    const result = drawBackdrop({
      backdrop,
      ctx,
      pointerLocation,
      elementToHighlight,
      isSelected,
      gpsSource,
      imageCache,
    });
    if (result.backdropUnderCursor) {
      backdropUnderCursor = result.backdropUnderCursor;
    }
  });
  if (backdropUnderCursor) {
    const mapElement: MapElement = {
      type: "backdrop",
      rect: backdropUnderCursor.rect,
      dragHandleUnderCursor: backdropUnderCursor.dragHandleUnderCursor,
      element: backdropUnderCursor.backdrop,
    };
    return {
      elementUnderPointerLocation: mapElement,
      snapLines: { vertical: undefined, horiztonal: undefined },
    };
  }
  return {
    elementUnderPointerLocation: undefined,
    snapLines: { vertical: undefined, horiztonal: undefined },
  };
};

interface DrawBackdropConfig {
  backdrop: Backdrop;
  ctx: CanvasRenderingContext2D;
  pointerLocation: Point | undefined;
  elementToHighlight: MapElement | undefined;
  isSelected: boolean;
  gpsSource?: GPSSource;
  data?: DataVisualization;
  imageCache?: Map<string, HTMLImageElement>;
}

const drawBackdrop = (
  config: DrawBackdropConfig
): {
  backdropUnderCursor:
    | {
        backdrop: Backdrop;
        dragHandleUnderCursor?: DragHandle;
        rect: Rect;
        point: Point;
      }
    | undefined;
  snapLines: {
    vertical: SnapLine | undefined;
    horiztonal: SnapLine | undefined;
  };
} => {
  const {
    backdrop,
    ctx,
    pointerLocation,
    elementToHighlight,
    isSelected,
    gpsSource,
  } = config;
  //backdrop.position is relative to the context/map size, e.g. 0.5, 0.5 is the center of the map
  // what is the actual screen size of the canvas?
  const borderWidth = backdrop.style.borderWidth;

  const x = backdrop.position.x * ctx.canvas.width + borderWidth / 2;
  const y = backdrop.position.y * ctx.canvas.height + borderWidth / 2;
  const width = backdrop.size.width * ctx.canvas.width - borderWidth;
  const height = backdrop.size.height * ctx.canvas.height - borderWidth;

  const rect = { x, y, width, height };
  const isUnderCursor = pointerLocation
    ? pointIsInsideRect(pointerLocation, rect)
    : false;

  ctx.fillStyle = backdrop.style.backgroundColor;
  ctx.globalAlpha = backdrop.style.opacity;
  ctx.globalAlpha = 1;

  ctx.strokeStyle = backdrop.style.borderColor;
  ctx.lineWidth = borderWidth;

  const maxAllowedRadius = Math.min(width, height) / 2;
  const cornerRadius = Math.max(
    0,
    Math.min(backdrop.style.cornerRadius, maxAllowedRadius)
  );

  ctx.lineJoin = cornerRadius > 0 ? "round" : "miter";

  ctx.beginPath();
  ctx.moveTo(x + cornerRadius, y);
  ctx.lineTo(x + width - cornerRadius, y);
  ctx.arcTo(x + width, y, x + width, y + cornerRadius, cornerRadius);
  ctx.lineTo(x + width, y + height - cornerRadius);
  ctx.arcTo(
    x + width,
    y + height,
    x + width - cornerRadius,
    y + height,
    cornerRadius
  );
  ctx.lineTo(x + cornerRadius, y + height);
  ctx.arcTo(x, y + height, x, y + height - cornerRadius, cornerRadius);
  ctx.lineTo(x, y + cornerRadius);
  ctx.arcTo(x, y, x + cornerRadius, y, cornerRadius);
  ctx.closePath();
  if (backdrop.style.borderWidth > 0) {
    ctx.stroke();
  }
  ctx.globalAlpha = backdrop.style.opacity;
  ctx.fill();
  ctx.globalAlpha = 1;

  if (backdrop.type === "dataVisualization" && gpsSource) {
    const config: DrawDataConfig = {
      ctx: ctx,
      rect,
      gpsSource: gpsSource,
      color: backdrop.style.foregroundColor,
      padding: backdrop.style.padding,
      cornerRadius: cornerRadius,
    };
    ctx.globalAlpha = backdrop.style.foregroundOpacity;
    drawData(config);
    ctx.globalAlpha = 1;
  }
  if (backdrop.type === "image") {
    const id = backdrop.style.imageName;
    const imageElement = config.imageCache?.get(id ?? "");

    if (imageElement) {
      const borderWidth = backdrop.style.borderWidth;
      const imageInset = borderWidth / 2;
      const innerX = x + imageInset;
      const innerY = y + imageInset;
      const innerWidth = width - borderWidth;
      const innerHeight = height - borderWidth;
      const innerRadius = Math.max(0, cornerRadius - imageInset);

      ctx.save();
      ctx.beginPath();
      ctx.moveTo(innerX + innerRadius, innerY);
      ctx.lineTo(innerX + innerWidth - innerRadius, innerY);
      ctx.arcTo(
        innerX + innerWidth,
        innerY,
        innerX + innerWidth,
        innerY + innerRadius,
        innerRadius
      );
      ctx.lineTo(innerX + innerWidth, innerY + innerHeight - innerRadius);
      ctx.arcTo(
        innerX + innerWidth,
        innerY + innerHeight,
        innerX + innerWidth - innerRadius,
        innerY + innerHeight,
        innerRadius
      );
      ctx.lineTo(innerX + innerRadius, innerY + innerHeight);
      ctx.arcTo(
        innerX,
        innerY + innerHeight,
        innerX,
        innerY + innerHeight - innerRadius,
        innerRadius
      );
      ctx.lineTo(innerX, innerY + innerRadius);
      ctx.arcTo(innerX, innerY, innerX + innerRadius, innerY, innerRadius);
      ctx.closePath();
      ctx.clip();

      // Aspect fill logic: crop and fill the entire rectangle
      let srcX = 0;
      let srcY = 0;
      let srcWidth = imageElement.width;
      let srcHeight = imageElement.height;

      const canvasAspect = innerWidth / innerHeight;
      const imageAspect = imageElement.width / imageElement.height;

      if (canvasAspect > imageAspect) {
        // Canvas is wider relative to image
        srcHeight = imageElement.width / canvasAspect;
        srcY = (imageElement.height - srcHeight) / 2;
      } else {
        // Canvas is taller relative to image
        srcWidth = imageElement.height * canvasAspect;
        srcX = (imageElement.width - srcWidth) / 2;
      }

      ctx.drawImage(
        imageElement,
        srcX,
        srcY,
        srcWidth,
        srcHeight,
        innerX,
        innerY,
        innerWidth,
        innerHeight
      );
      ctx.restore();
    }
  }
  if (isSelected) {
    const paddedRect = rectWithPadding(rect, 5);
    drawSelectedRect(paddedRect, ctx);

    const handleSize = 20;
    const handleWidth = 10;

    const leadingRect: Rect = {
      x: x - handleSize / 2 + handleWidth,
      y: y - handleSize / 2 + handleWidth,
      width: handleSize,
      height: handleSize,
    };
    drawResizeHandle(
      ctx,
      { x: leadingRect.x, y: leadingRect.y },
      handleSize,
      handleWidth,
      "top-left"
    );

    const trailingRect = {
      x: x + width - handleSize / 2,
      y: y + height - handleSize / 2,
      width: handleSize,
      height: handleSize,
    };
    drawResizeHandle(
      ctx,
      {
        x: trailingRect.x + handleSize - handleWidth / 2,
        y: trailingRect.y + handleSize - handleWidth / 2,
      },
      handleSize,
      handleWidth,
      "bottom-right"
    );

    if (pointerLocation) {
      let dragHandleUnderPointer: DragHandle | undefined = undefined;
      if (pointIsInsideRect(pointerLocation, leadingRect)) {
        dragHandleUnderPointer = { position: "leading" };
      } else if (pointIsInsideRect(pointerLocation, trailingRect)) {
        dragHandleUnderPointer = { position: "trailing" };
      }
      const result = {
        backdropUnderCursor:
          dragHandleUnderPointer || isUnderCursor
            ? {
                backdrop,
                rect,
                point: { x, y },
                dragHandleUnderCursor: dragHandleUnderPointer,
              }
            : undefined,
        snapLines: { vertical: undefined, horiztonal: undefined },
      };

      return result;
    }
  }
  return {
    backdropUnderCursor: isUnderCursor
      ? { backdrop, rect, point: { x, y } }
      : undefined,
    snapLines: { vertical: undefined, horiztonal: undefined },
  };
};
